import React, { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

/**
 * Audit-friendly delete confirmation for admin vehicle or parking listings.
 * listing: { type, listing_id, title }
 */
export default function AdminListingDeleteModal({ listing, onClose, onDelete }) {
  const [confirmText, setConfirmText] = useState("");
  const [force, setForce] = useState(false);
  const [needsForce, setNeedsForce] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!listing) {
      setConfirmText("");
      setForce(false);
      setNeedsForce(false);
    }
  }, [listing]);

  if (!listing) return null;

  const expected = listing.listing_id;
  const ok = confirmText.trim() === expected.trim();
  const typeLabel = listing.type === "parking" ? "parking listing" : "vehicle listing";

  const submit = async () => {
    if (!ok) return;
    setDeleting(true);
    try {
      await onDelete?.(listing, { force });
    } catch (e) {
      if (e?.message === "active_bookings_exist") {
        setNeedsForce(true);
        setForce(true);
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={!!listing} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-white border border-[var(--zc-border)] max-w-md" data-testid="admin-listing-delete-modal">
        <DialogHeader>
          <DialogTitle className="zc-headline text-2xl flex items-center gap-2" style={{ color: "var(--zc-danger,#dc2626)" }}>
            <AlertTriangle size={20} /> Delete {typeLabel}
          </DialogTitle>
        </DialogHeader>
        <div className="text-sm text-[var(--zc-text-2)] space-y-2">
          <p>
            You're about to remove <b className="text-[var(--zc-text)]">{listing.title}</b> from the platform.
            It will disappear from browse/search.
          </p>
          {needsForce && (
            <p className="text-xs p-3 rounded border border-[var(--zc-border)]" style={{ background: "rgba(245,158,11,0.08)", color: "var(--zc-warn,#f59e0b)" }}>
              This listing has active or confirmed bookings. Force delete will remove it anyway; existing bookings remain in the system for ops.
            </p>
          )}
        </div>
        {needsForce && (
          <label className="flex items-center gap-2 text-sm cursor-pointer mt-2" data-testid="admin-listing-delete-force">
            <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
            Force delete despite active bookings
          </label>
        )}
        <div className="mt-4">
          <label className="text-xs text-[var(--zc-text-3)] block mb-1">
            Type <b className="text-[var(--zc-text)] font-mono">{expected}</b> to confirm
          </label>
          <input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="zc-input h-10 px-3 w-full text-sm font-mono"
            data-testid="admin-listing-delete-confirm"
            autoFocus
          />
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="px-4 h-10 zc-btn-ghost text-sm">Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={!ok || deleting || (needsForce && !force)}
            className="px-5 h-10 text-sm rounded-md text-white disabled:opacity-40 transition-opacity"
            style={{ background: "var(--zc-danger,#dc2626)" }}
            data-testid="admin-listing-delete-submit"
          >
            {deleting ? "Deleting…" : needsForce ? "Force delete" : "Delete listing"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
